import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Dialog, InlineError, Input, Skeleton, Textarea } from '../../components/ui';
import { useAuth } from '../auth/AuthProvider';
import { useCreateSubmission } from './hooks';
import {
  reviewErrorMessage,
  sampleKeys,
  uploadSubmissionOption,
  useComponentTypes,
} from './sampleReview';
import {
  addOption,
  MAX_OPTIONS_PER_COMPONENT,
  buildStagedSlots,
  failedOptions,
  markOption,
  pendingUploads,
  removeOption,
  submitLabel,
} from './stagedUpload';
import { exchangeErrorMessage } from './types';
import type { StagedMap, StagedOption, StagedSlot } from './stagedUpload';

/**
 * Sending a run, in one dialog.
 *
 * What this replaces: a panel that unfolded inline, pushed the whole history
 * down the page, asked for a title and some notes, and then said "you can
 * upload the sample photos right after sending" — which meant finding the run
 * you had just created in a list and opening it. Two steps, in two places, for
 * one action.
 *
 * The two-step nature is real and cannot be removed: submission_options has a
 * NOT NULL foreign key to supplier_submissions and guard_option_insert reads
 * the parent's status, so no photo can exist before its run does. What can be
 * removed is the person having to know that. The files are held in the browser
 * (stagedUpload.ts), the row is written on Submit, and the photos follow
 * against its id without anyone leaving the dialog.
 *
 * Where the seam still shows is a partial failure, and it is shown rather than
 * hidden. Once the row is written it cannot be withdrawn — supplier_submissions
 * has no DELETE grant, deliberately, since TAP may already be reading it — so
 * a Drive upload that fails leaves a real submission with fewer photos than
 * intended. The dialog stays open, marks exactly which slots failed, and
 * retries only those.
 */

function messageOf(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

/**
 * A stable key for a staged option.
 *
 * Needed because the list index is not one: removing Option 2 shifts every
 * option after it, and React would reuse the wrong row's state. crypto is
 * available in every browser this app supports and in the test environment.
 */
function newOptionId(): string {
  return crypto.randomUUID();
}

export function NewSubmissionDialog({
  open,
  organizationId,
  onClose,
}: {
  open: boolean;
  organizationId: string;
  onClose: () => void;
}) {
  const componentsQuery = useComponentTypes();
  const create = useCreateSubmission();
  const { client, access } = useAuth();
  const queryClient = useQueryClient();

  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [staged, setStaged] = useState<StagedMap>({});
  // Non-null once the row exists. Kept across a retry so a second press of the
  // button uploads photos instead of creating a second run.
  const [submissionId, setSubmissionId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const createdBy = access.status === 'approved' ? access.profile.id : '';
  const components = (componentsQuery.data ?? []).map((component) => component.id);
  const slots = buildStagedSlots(components, staged);
  const created = submissionId !== null;

  function reset() {
    setTitle('');
    setNotes('');
    setStaged({});
    setSubmissionId(null);
    setBusy(false);
    setError('');
  }

  function close() {
    reset();
    onClose();
  }

  async function submit() {
    const trimmed = title.trim();
    if (trimmed === '') {
      setError('Give the submission a name first.');
      return;
    }
    setError('');
    setBusy(true);

    let id = submissionId;
    if (id === null) {
      try {
        const row = await create.mutateAsync({
          supplierOrganizationId: organizationId,
          title: trimmed,
          notes,
        });
        id = row.id;
        setSubmissionId(id);
      } catch (cause) {
        setError(exchangeErrorMessage(messageOf(cause)));
        setBusy(false);
        return;
      }
    }

    // One at a time, not Promise.all. Every upload calls ensureProjectFolder
    // against the same Drive folder name, and four of those racing would each
    // find nothing and each create one — leaving the supplier with four
    // folders called TAP Sample Photos and their photos scattered across them.
    let map = staged;
    let anyFailed = false;
    // The pending list is computed ONCE, before the loop. Recomputing it each
    // pass would re-read a map that the loop is itself editing, and an option
    // marked 'failed' mid-run would be picked straight back up — retrying
    // inside the same submit instead of waiting for the person to ask.
    for (const { component, option } of pendingUploads(buildStagedSlots(components, map))) {
      map = markOption(map, component, option.id, 'uploading');
      setStaged(map);
      try {
        await uploadSubmissionOption(client, {
          submissionId: id,
          componentType: component,
          file: option.file,
          createdBy,
        });
        map = markOption(map, component, option.id, 'uploaded');
      } catch (cause) {
        anyFailed = true;
        map = markOption(map, component, option.id, 'failed', reviewErrorMessage(messageOf(cause)));
      }
      setStaged(map);
    }

    void queryClient.invalidateQueries({ queryKey: sampleKeys.all });
    setBusy(false);

    if (anyFailed) {
      setError(
        'Your submission was sent, but some photos did not upload. Retry them below, or close this and add them from the submission in your list.',
      );
      return;
    }
    close();
  }

  const failures = failedOptions(slots);

  return (
    <Dialog
      open={open}
      wide
      title="New submission"
      onClose={busy ? () => undefined : close}
      footer={
        <>
          <Button variant="secondary" onClick={close} disabled={busy}>
            {created ? 'Close' : 'Cancel'}
          </Button>
          <Button
            onClick={() => {
              void submit();
            }}
            disabled={busy || (created && failures.length === 0)}
          >
            {submitLabel(slots, created, busy)}
          </Button>
        </>
      }
    >
      {error === '' ? null : <InlineError>{error}</InlineError>}

      <Input
        label="What are you sending?"
        placeholder="Mesh Bag v2 — pre-production samples"
        value={title}
        onChange={(event) => setTitle(event.target.value)}
        // The row is written on the first submit and its title cannot be
        // changed from this side afterwards (rename_submission is TAP-only),
        // so the field stops accepting edits once it means something.
        disabled={created || busy}
      />
      <Textarea
        label="Notes for TAP"
        rows={3}
        value={notes}
        onChange={(event) => setNotes(event.target.value)}
        disabled={created || busy}
      />

      <h3 className="new-submission-heading">Sample photos</h3>
      <p className="new-submission-hint">
        Send up to five options per component so TAP has something to choose between — or send the
        submission now and add photos from your list afterwards.
      </p>

      {componentsQuery.isPending ? (
        <Skeleton lines={4} label="Loading sample components" />
      ) : componentsQuery.isError || components.length === 0 ? (
        <InlineError>
          Sample components could not be loaded. You can still send the submission and add photos
          from your list afterwards.
        </InlineError>
      ) : (
        <ul className="new-submission-slots">
          {slots.map((slot) => (
            <StagedSlotRow
              key={slot.component}
              slot={slot}
              busy={busy}
              onAdd={(file) =>
                setStaged((map) => addOption(map, slot.component, file, newOptionId()))
              }
              onRemove={(id) => setStaged((map) => removeOption(map, slot.component, id))}
            />
          ))}
        </ul>
      )}
    </Dialog>
  );
}

const STATE_LABELS: Record<StagedOption['state'], string> = {
  ready: 'Ready to send',
  uploading: 'Uploading…',
  uploaded: 'Sent',
  failed: 'Upload failed',
};

const STATE_TONES: Record<StagedOption['state'], 'neutral' | 'brand' | 'success' | 'warning'> = {
  ready: 'brand',
  uploading: 'brand',
  uploaded: 'success',
  failed: 'warning',
};

/**
 * One component and its options.
 *
 * The options are numbered by their position in the list, which is also the
 * order they will be uploaded in and the order the reviewer will see them.
 * That is why removing a staged option renumbers the ones after it — the
 * numbers describe the list, not the files.
 */
function StagedSlotRow({
  slot,
  busy,
  onAdd,
  onRemove,
}: {
  slot: StagedSlot;
  busy: boolean;
  onAdd: (file: File) => void;
  onRemove: (id: string) => void;
}) {
  return (
    <li className="new-submission-slot">
      <div className="new-submission-slot-head">
        <span className="new-submission-slot-name">{slot.component}</span>
        <span className="new-submission-slot-count">
          {slot.options.length} of {MAX_OPTIONS_PER_COMPONENT}
        </span>
      </div>

      {slot.options.length === 0 ? (
        <p className="new-submission-slot-file">No photos chosen</p>
      ) : (
        <ol className="new-submission-options">
          {slot.options.map((option, index) => (
            <li
              key={option.id}
              className={`new-submission-option new-submission-option--${option.state}`}
            >
              <span className="new-submission-option-name">
                Option {index + 1}
                <span className="new-submission-option-file">{option.file.name}</span>
              </span>
              <Badge tone={STATE_TONES[option.state]}>{STATE_LABELS[option.state]}</Badge>
              {/* An uploaded option has a row in the database that this dialog
                  cannot delete, so there is nothing honest for Remove to do. */}
              {option.state === 'uploaded' ? null : (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={busy}
                  aria-label={`Remove option ${String(index + 1)} for ${slot.component}`}
                  onClick={() => onRemove(option.id)}
                >
                  Remove
                </Button>
              )}
              {option.error === '' ? null : (
                <p className="new-submission-slot-error">{option.error}</p>
              )}
            </li>
          ))}
        </ol>
      )}

      {slot.addLabel === null ? (
        // Said before the click rather than as a red error after it: the
        // database refuses a sixth option for the round.
        <p className="new-submission-slot-full">Five options is the limit for this component.</p>
      ) : (
        <label className="ui-button ui-button--secondary ui-button--sm">
          {slot.addLabel}
          <input
            type="file"
            accept="image/*"
            aria-label={`Add a photo for ${slot.component}`}
            disabled={busy}
            style={{ display: 'none' }}
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = '';
              if (file === undefined) return;
              onAdd(file);
            }}
          />
        </label>
      )}
    </li>
  );
}
